export const IMAGE_UPLOAD_MIN_PX = 400;
export const IMAGE_UPLOAD_MAX_PX = 5000;

// 宽高比限制（width/height）：0.5 ~ 2，含边界。0.5 与 2 互为倒数，故对宽/高方向对称。
export const IMAGE_UPLOAD_MIN_ASPECT_RATIO = 0.5;
export const IMAGE_UPLOAD_MAX_ASPECT_RATIO = 2;

export interface ImageDimensions {
  width: number;
  height: number;
}

export interface ImageDimensionBounds {
  min: number;
  max: number;
}

export interface ImageDimensionError extends ImageDimensions, ImageDimensionBounds {}

export interface ImageAspectRatioBounds {
  min: number;
  max: number;
}

export interface ImageAspectRatioError extends ImageDimensions, ImageAspectRatioBounds {
  ratio: number;
}

export interface AspectRatioToastParams {
  ns: string;
  min: number;
  max: number;
  width: number;
  height: number;
  ratio: string;
  // i18next t() 的 options 形参要求带字符串索引签名（$Dictionary），具名接口需显式声明
  [key: string]: string | number;
}

export function validateImageDimensions(
  dimensions: ImageDimensions,
  bounds: ImageDimensionBounds = { min: IMAGE_UPLOAD_MIN_PX, max: IMAGE_UPLOAD_MAX_PX },
): ImageDimensionError | null {
  const { width, height } = dimensions;
  const { min, max } = bounds;
  if (width < min || height < min || width > max || height > max) {
    return { min, max, width, height };
  }
  return null;
}

export function validateImageAspectRatio(
  dimensions: ImageDimensions,
  bounds: ImageAspectRatioBounds = {
    min: IMAGE_UPLOAD_MIN_ASPECT_RATIO,
    max: IMAGE_UPLOAD_MAX_ASPECT_RATIO,
  },
): ImageAspectRatioError | null {
  const { width, height } = dimensions;
  const { min, max } = bounds;
  // 非法尺寸（无法计算比例）按不通过处理，避免除零得到 Infinity/NaN
  if (width <= 0 || height <= 0) {
    return { min, max, width, height, ratio: 0 };
  }
  const ratio = width / height;
  if (ratio < min || ratio > max) {
    return { min, max, width, height, ratio };
  }
  return null;
}

/** 构造 common 命名空间下 imageAspectRatioInvalid 文案的 t() 参数（含 ns），供各上传位统一复用，避免重复。 */
export function aspectRatioToastParams(error: ImageAspectRatioError): AspectRatioToastParams {
  return {
    ns: 'common',
    min: error.min,
    max: error.max,
    width: error.width,
    height: error.height,
    ratio: error.ratio.toFixed(2),
  };
}

export function readImageFileDimensions(file: File): Promise<ImageDimensions> {
  return new Promise((resolve, reject) => {
    const objectURL = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(objectURL);
      resolve({ width: image.naturalWidth || image.width, height: image.naturalHeight || image.height });
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectURL);
      reject(new Error('image load failed'));
    };
    image.src = objectURL;
  });
}
